import get from 'lodash/get'
import includes from 'lodash/includes'
import isNil from 'lodash/isNil'
import isString from 'lodash/isString'
import toLower from 'lodash/toLower'
import toNumber from 'lodash/toNumber'
import toString from 'lodash/toString'
import OLFeature from 'ol/Feature'
import OLStyleCircle from 'ol/style/Circle'
import OLStyleFill from 'ol/style/Fill'
import OLStyleIcon from 'ol/style/Icon'
import OLStyleRegularShape from 'ol/style/RegularShape'
import OLStyleStroke from 'ol/style/Stroke'
import OLStyleStyle from 'ol/style/Style'
import OLStyleText from 'ol/style/Text'

import PoziAPIData from 'api/data'
import * as Model from 'api/model'
import * as PoziUtils from 'api/utils'
import { brightnessByColour } from 'utils/ColourPicker'

const DEFAULT_STROKE_WIDTH = 3
const DEFAULT_LINE_STROKE_OPACITY = 1
const DEFAULT_LINE_STROKE_WIDTH = 3
export const SILENT_UPDATE = true
const SELECTED_COLOR_RGBA = 'rgba(238,150,79,0.3)' // #ee964f
const DEFAULT_MARKER_STROKE_OPACITY = 1
const DEFAULT_MARKER_FILL_OPACITY = 0.5
const DEFAULT_POLYGON_FILL_OPACITY = 0.1

// Global variables related to layer colours
const colours = ['#653294', '#0062B1', '#0C797D', '#808900', '#FB9E00', '#C45100', '#AB149E', '#9F0500', '#FA28FF', '#7B64FF', '#009CE0', '#194D33', '#16A5A5', '#68BC00', '#B0BC00', '#FCC400', '#E27300', '#D33115', '#FDA1FF', '#AEA1FF', '#73D8FF', '#68CCCA', '#A4DD00', '#DBDF00', '#FCDC00', '#FE9200', '#F44E3B']

let colourIndexCounter = 0
const layerColours = {}

/**
 * Pozi API Adapter
 * Based on "standard" developed at geojson.io
 * See http://geojson.io/ for generating example GeoJSON files.
 */
function GeoJSONIO () {
  // Adapters
  const adapters = {
    getLayerDefaultColour: function (layerConf) {
      return getLayerDefaultColour(layerConf)
    },
    getLayerConfig: function () {
      return getLayerConfig()
    },
    getStyleFunction: function (layerConf) {
      return getStyleFunction(layerConf)
    },
    getStyleFunctionSelected: function (layerConf) {
      return getStyleFunctionSelected(layerConf)
    },
    getStyle: function (layerConf, feature, resolution, bSelected) {
      return getStyle(layerConf, feature, resolution, bSelected)
    }
  }

  /**
   * Get unique colour for layer
   * @param {Object} layerConf Layer config object
   */
  const getLayerDefaultColour = function (layerConf) {
    const { titleid } = layerConf || {}

    // Retrieve colour for layer if it has been defined already.
    let colour = layerColours[titleid]

    // If no colour has been found, get a new one and store it.
    if (!colour) {
      const colourIndex = colourIndexCounter % (colours.length - 1)
      colour = colours[colourIndex]
      layerColours[titleid] = colour
      colourIndexCounter++
    }
    return colour
  }

  /**
   * Get Layer config for GeoJSONIO URL
   * @param {string} url URL
   * E.g. http://localhost:3000/?site=staging&layers=http://localhost:3000/config/staging/test.json
   */
  const getLayerConfig = function () {
    return new Promise(function (resolve, reject) {
      console.log('GeoJSONIO().getLayerConfig. Detecting external layers in the url.')
      const urlLayers = Model.get('app.state.layers', null)
      let bFound = false
      for (const key in urlLayers) {
        const urlLayer = urlLayers[key]
        if (urlLayer && urlLayer !== '' && includes(urlLayer, 'http')) {
          bFound = true
          console.log('Found external geojsonio layerConf', urlLayer)
          decodeGeoJSONIOLayerURL(urlLayer, resolve, reject)
        }
      }

      if (!bFound) {
        console.log('GeoJSONIO().getLayerConfig. No external layers in the url.')
        return resolve()
      }
    })
  }

  const decodeGeoJSONIOLayerURL = function (url, resolve, reject) {
    console.log('decodeGeoJSONIOLayerURL', url)

    return PoziAPIData().ajax({
      url,
      error: function (err) {
        return reject(err)
      },
      success: function (data) {
        const layers = Model.get('config.layers')

        const layerConf = {
          title: get(data, 'title', PoziUtils.getPoziIDFromString(url)),
          titleid: PoziUtils.getPoziIDFromString(get(data, 'title', url)),
          group: 'External',
          type: 'GeoJSON',
          visible: true,
          zoomExtents: true,
          styleGeoJSONIO: true,
          legend: get(data, 'meta.legend', ''),
          config: {
            spatial: {
              url,
              id: 'id',
              label: get(data, 'meta.label', 'id'),
              exclude: ['marker-color', 'marker-size', 'marker-symbol', 'marker_color', 'marker_size', 'marker_symbol', 'stroke', 'stroke-width', 'stroke-opacity', 'fill', 'fill-opacity', 'stroke_width', 'stroke_opacity', 'fill_opacity']
            }
          }
        }

        layers.unshift(layerConf)
        return resolve()
      }
    })
  }

  const getStyleFunction = function (layerConf) {
    return function (feature, resolution) {
      // console.log('Get unselected style for feature', feature)
      return getStyle(layerConf, feature, resolution, false)
    }
  }

  const getStyleFunctionSelected = function (layerConf) {
    return function (feature, resolution) {
      // console.log('Get selected style for feature', feature)
      return getStyle(layerConf, feature, resolution, true)
    }
  }

  const getStyle = function (layerConf, feature, resolution, bSelected) {
    const olGeomGeometry = feature.getGeometry()
    const olGeomGeometryType = olGeomGeometry?.getType()

    // Defined in templates.json (or overridden per site)
    // const selectedFillRGBA = Model.get('config.layout.map.selection.fill.color', 'rgba(255,140,0,0.5)')
    // const selectedStrokeRGBA = Model.get('config.layout.map.selection.stroke.color', 'rgba(238,150,79,0.5)')
    let selectedStrokeRGBA = getValueForProperty(feature, ['marker-selection-rgba', 'marker_selection_rgba'], layerConf, ['markerSelectionRGBA'], 'rgba(238,150,79,0.3)')

    // See https://openlayers.org/en/latest/apidoc/ol.geom.html#.GeometryType
    // const markerOpacity = feature.get('marker-opacity') || 1
    // const markerSize = getValueForProperty(feature, ['marker-size', 'marker_size'], layerConf, ['markerSize'], null)
    const markerSize = toLower(feature.get('marker-size')) || null
    let markerSymbol = getValueForProperty(feature, ['marker-symbol', 'marker_symbol'], layerConf, ['markerSymbol'], 'none')
    if (!isNil(markerSize) && markerSymbol === 'none') {
      markerSymbol = 'circle'
      feature.set('marker-symbol', markerSymbol, SILENT_UPDATE)
    }

    // If a feature has a label property (as defined in the layer config
    // in 'styleGeoJSONIO?.labelProp), use the value of that field as the
    // lookup field for showing a label
    const featurePropArr = []
    const labelProp = layerConf?.styleGeoJSONIO?.labelProp || ''
    if (labelProp !== '') {
      featurePropArr.push(labelProp)
    }

    let label = toString(getValueForProperty(feature, featurePropArr, layerConf, [], ''))
    label = PoziUtils.getJSONSafeString(label)

    // Once the label dependent code below has been removed, we can remove the label variable
    // const label = ''

    switch (olGeomGeometryType) {
      case 'Point':
      case 'MultiPoint':
        if (label !== '' && markerSymbol === 'none') {
          selectedStrokeRGBA = 'rgba(0,0,255,1)'
          return getLabelStyle(layerConf, feature, bSelected, selectedStrokeRGBA, label)
        } else {
          return getPointStyle(layerConf, feature, bSelected, selectedStrokeRGBA, label)
        }
      case 'LineString':
      case 'MultiLineString':
      case 'LinearRing':
        return getLineStringStyle(layerConf, feature, bSelected, selectedStrokeRGBA, label)
      case 'Polygon':
      case 'MultiPolygon':
      case 'Circle':
        return getPolygonStyle(layerConf, feature, bSelected, selectedStrokeRGBA, label)
      case 'GeometryCollection': {
        // Return style for first geom of the collection since we currently cannot
        // handle returning multiple styles
        const geometries = olGeomGeometry?.getGeometries() || []
        const firstGeom = geometries[0]
        if (firstGeom) {
          const tmpFeature = new OLFeature()
          tmpFeature.setGeometry(firstGeom)
          console.log('getStyle GeometryCollection: only style for first geometry is created', geometries)
          return getStyle(layerConf, tmpFeature, resolution, bSelected)
        }
        break
      }
      default:
        console.error('GeoJSONIO geometry type not supported.', olGeomGeometryType, feature.getGeometry())
        break
    }
  }

  // https://openlayers.org/en/latest/examples/vector-labels.html
  const getLabelStyle = function (layerConf, feature, bSelected, selectedStrokeRGBA, label) {
    let color = getValueForProperty(feature, ['style-color', 'label-color'], layerConf, ['labelColor'], null)
    // Only call getLayerDefaultColour if a colour has not been provided. This
    // avoids unneccesary colour lookups
    if (!color) {
      color = getLayerDefaultColour(layerConf)
    }
    const align = getValueForProperty(feature, ['style-label-align'], layerConf, ['labelAlign'], 'center')
    const baseline = getValueForProperty(feature, ['style-label-baseline'], layerConf, ['labelBaseline'], 'middle')
    const offsetX = getValueForProperty(feature, ['style-label-offset-x'], layerConf, ['labelOffsetX'], 0)
    const offsetY = getValueForProperty(feature, ['style-label-offset-y'], layerConf, ['labelOffsetY'], 0)
    const placement = getValueForProperty(feature, ['style-label-placement'], layerConf, ['labelPlacement'], undefined)
    const maxAngle = getValueForProperty(feature, ['style-label-max-angle'], layerConf, ['labelMaxangle'], undefined)
    const overflow = getValueForProperty(feature, ['style-label-overflow'], layerConf, ['labelOverflow'], true)
    const rotation = getValueForProperty(feature, ['style-label-rotation'], layerConf, ['labelRotation'], 0)
    const weight = getValueForProperty(feature, ['style-label-weight'], layerConf, ['labelWeight'], 'bold')
    let size = getValueForProperty(feature, ['style-label-size'], layerConf, ['labelSize'], '16px') + ''
    if (!size.includes('px')) {
      size += 'px'
    }

    const font = getValueForProperty(feature, ['style-label-font'], layerConf, ['labelFont'], 'Arial')
    const fontFinal = weight + ' ' + size + ' ' + font

    if (bSelected) {
      color = selectedStrokeRGBA
    }

    const haloColor = brightnessByColour(color) > 200 ? '#000000' : '#ffffff'

    const textStyle = new OLStyleText({
      text: label,
      fill: new OLStyleFill({ color }),
      stroke: new OLStyleStroke({ color: haloColor, width: 3 }), // Halo effect
      textAlign: align,
      textBaseline: baseline,
      font: fontFinal,
      offsetX,
      offsetY,
      placement,
      maxAngle,
      overflow,
      rotation
    })

    return [
      new OLStyleStyle({
        text: textStyle
      })
    ]
  }

  const getPointStyle = function (layerConf, feature, bSelected, selectedStrokeRGBA, label) {
    let markerColorHex = getValueForProperty(feature, ['marker_color', 'marker-color', 'style_color', 'style-color', 'stroke-color'], layerConf, ['markerColorHex', 'fillHex'], null)
    if (!markerColorHex) {
      markerColorHex = getLayerDefaultColour(layerConf)
    }
    const markerStrokeWidth = getValueForProperty(feature, ['marker_width', 'marker-width', 'marker_stroke_width', 'marker-stroke-width', 'style_width'], layerConf, ['markerStrokeWidth', 'markerWidth'], 2)
    const markerColorFillHex = getValueForProperty(feature, ['marker_color', 'marker-color', 'style_color', 'style-color', 'fill-color'], layerConf, ['markerColorFillHex', 'fillHex', 'markerColorHex'], markerColorHex)
    const markerStrokeOpacity = getValueForProperty(feature, ['marker-opacity', 'style-opacity', 'marker_opacity', 'style_opacity', 'stroke-opacity'], layerConf, ['markerStrokeOpacity', 'markerOpacity'], DEFAULT_MARKER_STROKE_OPACITY)
    const markerFillOpacity = getValueForProperty(feature, ['marker-opacity', 'style-opacity', 'marker_opacity', 'style_opacity', 'fill-opacity'], layerConf, ['markerFillOpacity', 'markerOpacity', 'fillOpacity'], DEFAULT_MARKER_FILL_OPACITY)
    const markerColorRGBAStroke = hexToRgbA(markerColorHex, markerStrokeOpacity)
    const markerColorRGBAFill = hexToRgbA(markerColorFillHex, markerFillOpacity)
    const markerSize = getValueForProperty(feature, ['marker-size', 'style-size', 'marker_size', 'style_size'], layerConf, ['markerSize', 'strokeWidth'], 'normal')
    let markerSizeInt = 0
    const markerSymbol = toLower(getValueForProperty(feature, ['marker-symbol', 'style-symbol', 'marker_symbol', 'style_symbol'], layerConf, ['markerSymbol'], 'circle'))
    const markerURL = toLower(getValueForProperty(feature, ['marker-url', 'marker_url'], layerConf, ['markerURL'], ''))

    // Check if markerSize is already an integer
    if (typeof markerSize === 'number') {
      markerSizeInt = markerSize
    } else {
      // Convert marker size to integer
      switch (markerSize) {
        case 'small':
          markerSizeInt = 6
          break
        case 'normal':
          markerSizeInt = 10
          break
        case 'large':
          markerSizeInt = 14
          break
        default:
          // Default to medium sized icons
          markerSizeInt = 10
      }
    }

    // Construct fill
    const fill = new OLStyleFill({
      color: markerColorRGBAFill
    })

    // Construct stroke
    const stroke = new OLStyleStroke({
      color: markerColorRGBAStroke,
      width: markerStrokeWidth
    })

    const strokeSelected = new OLStyleStroke({
      color: SELECTED_COLOR_RGBA,
      width: markerStrokeWidth + 6
    })

    // Construct marker icons
    let icon = null
    let selectedIcon = null
    const shapeStyle = []
    const markerStyle = []

    if (markerURL !== '') {
      const markerIcon = new OLStyleIcon({
        color: markerColorHex,
        size: [32, 32],
        src: markerURL
      })
      const markerFill = new OLStyleFill({
        color: markerColorHex || ''
      })
      const markerHaloIcon = new OLStyleCircle({
        radius: markerSizeInt + 4,
        stroke,
        fill: markerFill
      })

      if (bSelected) {
        selectedIcon = new OLStyleCircle({
          radius: markerSizeInt + 8,
          stroke: strokeSelected
        })
        shapeStyle.push(new OLStyleStyle({ image: selectedIcon }))
      }
      shapeStyle.push(new OLStyleStyle({ image: markerHaloIcon }))
      shapeStyle.push(new OLStyleStyle({ image: markerIcon }))
    } else {
      switch (markerSymbol) {
        case 'square':
          icon = new OLStyleRegularShape({
            fill,
            stroke,
            points: 4,
            radius: markerSizeInt,
            angle: Math.PI / 4
          })
          break
        case 'diamond':
          icon = new OLStyleRegularShape({
            fill,
            stroke,
            points: 4,
            radius: markerSizeInt,
            angle: Math.PI / 2
          })
          break
        case 'triangle':
          icon = new OLStyleRegularShape({
            fill,
            stroke,
            points: 3,
            radius: markerSizeInt,
            rotation: 0,
            angle: 0
          })
          break
        case 'hexagon':
          icon = new OLStyleRegularShape({
            fill,
            stroke,
            points: 6,
            radius: markerSizeInt,
            rotation: 0,
            angle: 0
          })
          break
        case 'star':
          icon = new OLStyleRegularShape({
            fill,
            stroke,
            points: 5,
            radius1: markerSizeInt,
            radius2: markerSizeInt / 2,
            angle: 0
          })
          break
        case 'heptagon':
          icon = new OLStyleRegularShape({
            fill,
            stroke,
            points: 7,
            radius1: markerSizeInt,
            radius2: markerSizeInt / 2,
            angle: 0
          })
          break
        case 'cross':
        case 'x':
          icon = new OLStyleRegularShape({
            fill,
            stroke,
            points: 4,
            radius: markerSizeInt,
            radius2: 0,
            angle: Math.PI / 4
          })
          break
        default:
          icon = new OLStyleCircle({
            radius: markerSizeInt,
            fill,
            stroke
          })
      }

      if (bSelected) {
        selectedIcon = new OLStyleCircle({
          radius: markerSizeInt + 4,
          stroke: strokeSelected
        })
        shapeStyle.push(new OLStyleStyle({ image: selectedIcon }))
      } else {
        // shapeStyle.push(new OLStyleStyle({ image: haloIcon }))
      }
      shapeStyle.push(new OLStyleStyle({ image: icon }))
    }

    let labelStyle = []
    if (label !== '') {
      labelStyle = getLabelStyle(layerConf, feature, bSelected, selectedStrokeRGBA, label)
    }

    const mainStyle = []
    if (!isNil(shapeStyle)) {
      mainStyle.push(...shapeStyle)
    }
    if (!isNil(markerStyle)) {
      mainStyle.push(...markerStyle)
    }
    if (!isNil(labelStyle)) {
      mainStyle.push(...labelStyle)
    }
    return mainStyle
  }

  // See createEditingStyle in https://github.com/openlayers/openlayers/blob/v5.3.0/src/ol/style/Style.js
  const getLineStringStyle = function (layerConf, feature, bSelected, selectedStrokeRGBA, label) {
    const lineDash = getLineDash(layerConf, feature)
    let strokeHex = getValueForProperty(feature, ['stroke', 'style-color', 'style_color', 'stroke-color'], layerConf, ['strokeHex'], null)
    if (!strokeHex) {
      strokeHex = getLayerDefaultColour(layerConf)
    }
    const strokeOpacity = getValueForProperty(feature, ['stroke-opacity', 'style-opacity', 'stroke_opacity', 'style_opacity'], layerConf, ['strokeOpacity'], DEFAULT_LINE_STROKE_OPACITY)
    const strokeRGBA = hexToRgbA(strokeHex, strokeOpacity)
    const strokeWidth = getValueForProperty(feature, ['stroke-width', 'style-width', 'stroke_width', 'style_width'], layerConf, ['strokeWidth'], DEFAULT_STROKE_WIDTH)

    // Orange halo
    const strokeOutlineHex = getValueForProperty(feature, ['style-outline-color', 'style_outline_color'], layerConf, ['strokeOutlineHex'], SELECTED_COLOR_RGBA)
    const strokeOutlineOpacity = getValueForProperty(feature, ['style-outline-opacity', 'style_outline_opacity'], layerConf, ['strokeOutlineOpacity'], DEFAULT_LINE_STROKE_OPACITY)
    const strokeOutlineRGBA = hexToRgbA(strokeOutlineHex, strokeOutlineOpacity)
    const strokeOutlineWidth = getValueForProperty(feature, ['style-outline-width', 'style_outline_width'], layerConf, ['strokeOutlineWidth'], 0)

    const strokeOutlineObj = {}
    const strokeObj = {}
    if (lineDash) {
      strokeOutlineObj.lineDash = lineDash
      strokeObj.lineDash = lineDash
    }
    strokeOutlineObj.color = strokeOutlineRGBA
    strokeOutlineObj.width = strokeOutlineWidth
    strokeObj.color = strokeRGBA
    strokeObj.width = strokeWidth

    const mainStyle = []
    if (bSelected) {
      const selectedStrokeObj = {
        color: SELECTED_COLOR_RGBA,
        width: strokeWidth + 8
      }
      const olStyleSelected = new OLStyleStyle({ stroke: new OLStyleStroke(selectedStrokeObj) })
      mainStyle.push(olStyleSelected)
    }
    mainStyle.push(new OLStyleStyle({ stroke: new OLStyleStroke(strokeOutlineObj) }))
    mainStyle.push(new OLStyleStyle({ stroke: new OLStyleStroke(strokeObj) }))

    let labelStyle = []
    if (label !== '') {
      labelStyle = getLabelStyle(layerConf, feature, bSelected, selectedStrokeRGBA, label)
    }

    return [...mainStyle, ...labelStyle]
  }

  const getLineDash = function (layerConf, feature) {
    let lineDash = getValueForProperty(feature, ['linedash'], layerConf, ['lineDash'], null)
    if (!isNil(lineDash)) {
      if (isString(lineDash)) {
        try {
          lineDash = JSON.parse(lineDash)
        } catch (err) {
          console.log('Failed to parse linedash string', lineDash)
        }
      }
    }
    return lineDash
  }

  const getPolygonStyle = function (layerConf, feature, bSelected, selectedStrokeRGBA, label) {
    let strokeHex = getValueForProperty(feature, ['stroke', 'style-color', 'style_color', 'stroke-color'], layerConf, ['strokeHex'], null)
    if (!strokeHex) {
      strokeHex = getLayerDefaultColour(layerConf)
    }
    const strokeOpacity = getValueForProperty(feature, ['stroke-opacity', 'style-opacity', 'stroke_opacity', 'style_opacity'], layerConf, ['strokeOpacity'], DEFAULT_LINE_STROKE_OPACITY)
    const strokeRGBA = hexToRgbA(strokeHex, strokeOpacity)
    const strokeWidth = getValueForProperty(feature, ['stroke-width', 'style-width', 'stroke_width', 'style_width'], layerConf, ['strokeWidth'], DEFAULT_LINE_STROKE_WIDTH)
    const fillHex = getValueForProperty(feature, ['fill', 'style-fill', 'style-fill-color', 'style_fill_color', 'fill-color'], layerConf, ['fillHex'], strokeHex)
    const fillOpacity = getValueForProperty(feature, ['fill-opacity', 'style-fill-opacity', 'style_fill_opacity'], layerConf, ['fillOpacity'], DEFAULT_POLYGON_FILL_OPACITY)
    const fillRGBA = hexToRgbA(fillHex, fillOpacity)

    const lineDash = getLineDash(layerConf, feature)

    // Orange halo
    const strokeOutlineHex = getValueForProperty(feature, ['style-outline-color', 'style_outline_color'], layerConf, ['strokeOutlineHex'], SELECTED_COLOR_RGBA)
    const strokeOutlineOpacity = getValueForProperty(feature, ['style-outline-opacity', 'style_outline_opacity'], layerConf, ['strokeOutlineOpacity'], DEFAULT_LINE_STROKE_OPACITY)
    const strokeOutlineRGBA = hexToRgbA(strokeOutlineHex, strokeOutlineOpacity)
    const strokeOutlineWidth = getValueForProperty(feature, ['style-outline-width', 'style_outline_width'], layerConf, ['strokeOutlineWidth'], 0)

    const strokeOutlineObj = {}
    const strokeObj = {}
    if (lineDash) {
      strokeOutlineObj.lineDash = lineDash
      strokeObj.lineDash = lineDash
    }
    strokeOutlineObj.color = strokeOutlineRGBA
    strokeOutlineObj.width = strokeOutlineWidth
    strokeObj.color = strokeRGBA
    strokeObj.width = strokeWidth

    const mainStyle = []
    if (bSelected) {
      const selectedStrokeObj = {
        color: SELECTED_COLOR_RGBA,
        width: strokeWidth + 8
      }
      const olStyleSelected = new OLStyleStyle({ stroke: new OLStyleStroke(selectedStrokeObj) })
      mainStyle.push(olStyleSelected)
    }
    mainStyle.push(new OLStyleStyle({ stroke: new OLStyleStroke(strokeOutlineObj) }))
    mainStyle.push(new OLStyleStyle({ fill: new OLStyleFill({ color: fillRGBA }), stroke: new OLStyleStroke(strokeObj) }))

    let labelStyle = []
    if (label !== '') {
      labelStyle = getLabelStyle(layerConf, feature, bSelected, selectedStrokeRGBA, label)
    }

    return [...mainStyle, ...labelStyle]
  }

  const hexToRgbA = function (hex, opacity) {
    // If the code is already in rgba, then just return it.
    if (includes(hex, 'rgba')) {
      return hex
    }
    let c
    if (/^#([A-Fa-f0-9]{3}){1,2}$/.test(hex)) {
      c = hex.substring(1).split('')
      if (c.length === 3) {
        c = [c[0], c[0], c[1], c[1], c[2], c[2]]
      }
      c = '0x' + c.join('')
      return 'rgba(' + [(c >> 16) & 255, (c >> 8) & 255, c & 255].join(',') + ',' + opacity + ')'
    }
    return 'rgba(255, 255, 255, 0.8)'
  }

  /**
   * Get a value for the property. First check if a value exists in layerConf.styleGeoJSONIO.[layerPropArr]
   * If no value is found in the layerConf, then check for a value in the feature: feature.get('[featurePropArr]')
   * Finally, if no value is found return the defaultValue.
   *
   * @param {OpenLayer Feature} feature
   * @param {String[]} featurePropArr
   * @param {PoziLayer} layerConf
   * @param {String[]} layerPropArr without styleGeoJSONIO
   * @param {String} defaultValue
   */
  const getValueForProperty = function (feature, featurePropArr, layerConf, layerPropArr, defaultValue) {
    let value = null
    // First check if the layerConf has a style.
    for (const j in layerPropArr) {
      const layerProp = 'styleGeoJSONIO.' + layerPropArr[j]
      value = get(layerConf, layerProp, null)
      if (!isNil(value)) {
        break
      }
    }

    // Then check if the data has a style.
    if (isNil(value)) {
      for (const i in featurePropArr) {
        const featureProp = featurePropArr[i]
        value = PoziUtils.getFeaturePropertyValueCaseInsensitive(feature, featureProp)
        if (!isNil(value)) {
          break
        }
      }
    }

    // If no styling in the layerConf or the data, then return the default value.
    if (isNil(value) || value === '') {
      return defaultValue
    }

    const valueAsNumber = toNumber(value)
    if (!isNaN(valueAsNumber)) {
      value = valueAsNumber
    }

    return value
  }

  // Return adapters (must be at end of adapter)
  return adapters
}

export default GeoJSONIO
// End A (Adapter)
